import React, { FC, useLayoutEffect, useRef, useState } from 'react';
import { Box, Typography, TypographyProps } from '@mui/material';
import { styled } from '@mui/material/styles';

const LabelText = styled(Typography)({
  width: '100%',
  minWidth: 0,
  textAlign: 'center',
  overflow: 'hidden',
});

/** A bit wider than a normal space, used when the whole name is visible. */
const FULL_NAME_GAP_EM = 0.45;
/** A bit tighter than a normal space, used between the ellipsis and the extension. */
const TRUNCATED_NAME_GAP_EM = 0.1;

export interface FileDisplayLabelProps {
  name: string;
  extension?: string;
  sx?: TypographyProps['sx'];
  onClick?: TypographyProps['onClick'];
}

/** `name (ext)` — only the name ellipsizes when the row is too narrow. */
export const FileDisplayLabel: FC<FileDisplayLabelProps> = ({ name, extension, sx, onClick }) => {
  const nameRef = useRef<HTMLSpanElement>(null);
  const extensionRef = useRef<HTMLSpanElement>(null);
  const [nameTruncated, setNameTruncated] = useState(false);

  useLayoutEffect(() => {
    const nameEl = nameRef.current;
    if (!nameEl) return;

    const update = () => {
      const extEl = extensionRef.current;
      if (!extEl) {
        setNameTruncated(false);
        return;
      }

      const overflowing = nameEl.scrollWidth > nameEl.clientWidth + 1;
      setNameTruncated(prev => {
        if (!prev) return overflowing;

        const fontSize = parseFloat(getComputedStyle(extEl).fontSize) || 16;
        const gapDelta = (FULL_NAME_GAP_EM - TRUNCATED_NAME_GAP_EM) * fontSize;
        return nameEl.scrollWidth > nameEl.clientWidth - gapDelta + 1;
      });
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(nameEl);
    if (nameEl.parentElement) observer.observe(nameEl.parentElement);
    return () => observer.disconnect();
  }, [name, extension]);

  return (
    <LabelText variant="subtitle2" onClick={onClick} sx={sx}>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          maxWidth: '100%',
          minWidth: 0,
          verticalAlign: 'bottom',
        }}
      >
        <Box
          component="span"
          ref={nameRef}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {name}
        </Box>
        {extension ? (
          <Box
            component="span"
            ref={extensionRef}
            sx={{
              flexShrink: 0,
              whiteSpace: 'nowrap',
              marginLeft: nameTruncated ? `${TRUNCATED_NAME_GAP_EM}em` : `${FULL_NAME_GAP_EM}em`,
            }}
          >
            {`(${extension})`}
          </Box>
        ) : null}
      </Box>
    </LabelText>
  );
};
